import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DataSourceService } from '../../../service/datatsource.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  FusionTransformationService,
  NotificationService,
  TabulatorService
} from '@nrwl/common-lib';
import { ViaviTableComponent } from '@nrwl/viavi-tabulator';

@Component({
  selector: 'nrwl-view-results-dialog',
  templateUrl: './view-results-dialog.component.html',
  styleUrls: ['./view-results-dialog.component.scss']
})
export class ViewResultsDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild(ViaviTableComponent) table: ViaviTableComponent;
  csvFileData: any[] = [];
  successCount = 0;
  failureCount = 0;
  skippedCount = 0;
  isLoading = false;
  viewLastResultsList: any[] = [];
  mappingTable: any;
  csvFileName: string;
  isViewLastResults = false;
  lastResultsDownloadFilePath = '/vibe/inventory/bulkconfig/files?type=config&groupName=rename';
  initialised = false;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private dialogRef: MatDialogRef<ViewResultsDialogComponent>,
    private dataSourceService: DataSourceService,
    private readonly tabulatorService: TabulatorService,
    private readonly notificationService: NotificationService,
    private fusionTransformationService: FusionTransformationService,
    private cdr: ChangeDetectorRef, // Inject ChangeDetectorRef
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    @Inject(MAT_DIALOG_DATA) public data: any // Inject the data passed when opening the dialog
  ) {
    this.mappingTable = this.tabulatorService.commonTableConfiguration();
    this.mappingTable.persistenceID = 'uploads-view-results';
    this.mappingTable.sort = true;
    this.mappingTable.page = true;
    this.mappingTable.height = 470;
    this.mappingTable.rowHeight = 40;
    this.mappingTable.initialSort = [{ column: 'status', dir: 'desc' }];
  }

  ngAfterViewInit(): void {
    this.initialised = true;
    this.csvFileName = this.data.fileNameData;
    this.getViewLastResults(this.csvFileName);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  getViewLastResults(fileName: string): any {
    const resultFileName = fileName.replace('.csv', '_results.csv');
    this.isLoading = true;
    this.dataSourceService
      .getViewLastResults(resultFileName)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.isLoading = false;
          this.viewLastResultsResponse(response);
        },
        error => {
          this.isLoading = false;
          this.notificationService.onFail('Error fetching results');
          this.dialogRef.close();
        }
      );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  viewLastResultsResponse(response: any): any {
    if (response?.details?.length > 0) {
      this.isViewLastResults = true;
      this.csvFileData = response.details;
      this.successCount = 0;
      this.failureCount = 0;
      this.skippedCount = 0;
      this.viewLastResultsList = this.transformSummaryData(this.csvFileData);

      this.csvFileData.forEach(item => {
        if (item.status === 'success') {
          this.successCount++;
        } else if (item.status === 'failure') {
          this.failureCount++;
        } else if (item.status === 'skipped') {
          this.skippedCount++;
        }
      });

      const imagesData = this.fusionTransformationService.transformData(
        'uploads-view-results',
        this.viewLastResultsList
      );
      if (imagesData && imagesData.column && imagesData.data) {
        this.mappingTable.columns = imagesData.column;
        this.mappingTable.data = imagesData.data;
        this.mappingTable = Object.assign({}, this.mappingTable);
      }
    } else {
      this.notificationService.onFail('View results not found');
      this.dialogRef.close();
    }
    this.cdr.detectChanges();
  }

  transformSummaryData(data: any[]): any {
    // const template = this.getSummaryTemplate();
    const temp: any[] = [];
    data.forEach(item => {
      temp.push(this.getCSVFileData(item));
    });
    // template.data = temp;
    return temp;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getCSVFileData(item: any): any {
    const headers = Object.keys(item);
    const key =
      headers && headers.length ? headers[headers.findIndex(k => k.indexOf('key:') === 0)] : '';
    return {
      applyTime: this.fusionTransformationService.getISODateTimeFormat(item['timestamp']),
      key: item[key || 'key'],
      model: item['model'],
      originalName: item['original-name'],
      newName: item['name'],
      overWrite: item['overwrite'],
      status: this.getCSVFileStatus(item['status']),
      reason: item['reason']
    };
  }

  getCSVFileStatus(status: string): any {
    switch (status) {
      case 'success':
        return 'success';
      case 'failure':
        return 'failure';
      case 'skipped':
        return 'skipped';
    }
  }

  downloadViewLastResultFile(): any {
    const csvFileNameObj = this.csvFileName.split('.');
    const csvFileLabelName = csvFileNameObj[0];
    const downloadAnchor = document.createElement('a');

    // Set the download URL and initiate the download
    downloadAnchor.href = `${this.lastResultsDownloadFilePath}&fileName=${csvFileLabelName}_results.csv`;
    downloadAnchor.setAttribute('download', `${csvFileLabelName}_results.csv`);
    document.body.appendChild(downloadAnchor); // Append to the body
    downloadAnchor.click(); // Trigger the download
    document.body.removeChild(downloadAnchor); // Clean up

    // Show success message
    this.notificationService.onSuccess('View last results downloaded successfully');
    this.dialogRef.close(); // Close the dialog after download
  }

  copyToClipboard(): any {
    if (this.csvFileData && this.csvFileData.length) {
      if (navigator.clipboard) {
        // Extract headers from the first row
        const headers = Object.keys(this.csvFileData[0]);
        let csv = headers.join('\t') + '\n';

        // Add the data rows
        this.csvFileData.forEach(row => {
          csv += headers.map(header => row[header]).join('\t') + '\n';
        });

        // Write to the clipboard
        navigator.clipboard
          .writeText(csv)
          .then(() => {
            this.notificationService.onSuccess('Copy to clipboard was successful');
          })
          .catch(err => {
            this.notificationService.onFail('Could not copy to clipboard');
          });
      } else {
        this.notificationService.onFail('This browser does not support copy to clipboard');
      }
    } else {
      this.notificationService.onFail('There is no data to copy to clipboard');
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
